import _ from 'lodash';
import { contentTypes } from 'utils/contentTypeConstants';
import { TABS } from 'utils/tabsConstants';
import {
  LOT_TYPES,
  LOT_TYPES_LABELS,
  VEHICLE_TYPES,
} from 'utils/lotTypesConstants';

const enabled = value => {
  const {
    auctionYear,
    liveAuctionVenue,
    auctionType,
    hasReservePrice,
    onlineBiddingAvailable,
    onlineAuctionName,
  } = value;
  return (
    !!_.head(auctionYear) ||
    !!_.head(liveAuctionVenue) ||
    !!_.head(onlineAuctionName) ||
    !!_.head(auctionType) ||
    hasReservePrice !== undefined ||
    onlineBiddingAvailable !== undefined
  );
};

const NUMBER_OF_MARKS = 30;
const MAX_ESTIMATE_PRICE = 3000000;

const getRangeData = estimates => {
  const marksPrices = [];
  const marksCounts = [];

  if (estimates.length === 0)
    return {
      initialData: { min: 0, max: 0 },
      marksCounts,
      marksPrices,
      step: 0,
      maxMark: 0,
    };

  const min = 0;
  const lowerBound = 30000;
  const higherBound = MAX_ESTIMATE_PRICE;
  const realMax = estimates[estimates.length - 1].value;

  // round the max price to the nearest multiple of lowerbound
  const tempMax =
    Math.ceil(estimates[estimates.length - 1].value / lowerBound) * lowerBound;

  const max =
    tempMax > higherBound
      ? higherBound
      : tempMax < lowerBound
      ? lowerBound
      : tempMax;
  const step = Math.floor((max - min) / NUMBER_OF_MARKS);
  const maxMark = step * NUMBER_OF_MARKS;

  let currentStep = step;
  let count = 0;
  let i = 0;
  while (currentStep <= maxMark && i < estimates.length) {
    if (estimates[i].value <= currentStep) {
      count += estimates[i].count;
      i++;
    } else {
      marksPrices.push(currentStep);
      marksCounts.push(count);
      count = 0;
      currentStep += step;
    }
  }
  // push count of the last step
  if (count !== 0) {
    marksPrices.push(currentStep);
    marksCounts.push(count);
  }
  //add counts of prices > max mark
  count = 0;
  while (i < estimates.length) {
    count += estimates[i].count;
    i++;
  }
  marksCounts.push(marksCounts.pop() + count);
  const initialData = { min, max: marksPrices[marksPrices.length - 1] };
  return { marksCounts, marksPrices, initialData, step, maxMark, realMax };
};

const PRIVATE_SALE_LISTING = 'Private Sale';

const getTag = vehicle => {
  if (vehicle?.auctionType === PRIVATE_SALE_LISTING) {
    return !vehicle.salePrice || vehicle.askingPriceOverride ? 'AVAILABLE' : 'SOLD';
  }

  if (vehicle.salePrice) return '';

  const today = Date.now();
  const auctionEnded = !!vehicle.auctionEndDate;
  
  if (
    vehicle.auctionStartDate <= today &&
    vehicle.upcomingAuctionEndDate >= today &&
    !auctionEnded
  ) return 'AVAILABLE';

  if (vehicle.auctionStartDate > today) return 'UPCOMING';

  if (vehicle.askingPrice || vehicle.askingPriceOverride) return 'AVAILABLE';

  return '';
};

const getAuction = (vehicle, isNotTimestamp = false) => {
  const year = isNotTimestamp
    ? new Date(vehicle.auctionStartDate).getFullYear()
    : new Date(parseInt(vehicle.auctionStartDate)).getFullYear();
  switch (vehicle.auctionType) {
    case PRIVATE_SALE_LISTING:
      return PRIVATE_SALE_LISTING;
    default:
      return vehicle.auctionYear
        ? `${
            !Number.isNaN(vehicle.auctionYear) ? vehicle.auctionYear : ''
          } \xa0 | \xa0 ${vehicle.auctionName}`
        : `${!Number.isNaN(year) ? year : ''} \xa0 | \xa0 ${
            vehicle.auctionName
          }`;
  }
};

const getPrice = (vehicle, currency = 'USD') => {
  if (vehicle.salePrice)
    return `SOLD ${
      !vehicle.privateSalesPrice
        ? ` ${getPriceFormatted(vehicle.salePrice, currency)}`
        : ''
    }`;
  else {
    if (vehicle.askingPriceOverride) return `Price Available Upon Request`;
    if (vehicle.askingPrice)
      return `Asking Price - ${
        getPriceFormatted(vehicle.askingPrice, currency) ?? ''
      }`;
    if (vehicle.lowEstimate || vehicle.highEstimate || vehicle.uniqueEstimate) {
      const reserve = `${vehicle.hasReservePrice ? '' : 'Without Reserve'}`;
      const estimates =
        vehicle.uniqueEstimate ||
        getEstimates(vehicle.lowEstimate, vehicle.highEstimate, currency);
      return `${estimates} ${reserve && `| ${reserve}`}`;
    }
  }
  return '';
};

const getEstimates = (lowEstimate, highEstimate, currency) => {
  if (
    lowEstimate &&
    lowEstimate !== '0' &&
    highEstimate &&
    highEstimate !== '0'
  )
    return `${getPriceFormatted(lowEstimate, currency)} - ${getPriceFormatted(
      highEstimate,
      currency
    )}`;
  else if (lowEstimate && lowEstimate !== '0')
    return `${getPriceFormatted(lowEstimate, currency)}`;
  else if (highEstimate && highEstimate !== '0')
    return `${getPriceFormatted(highEstimate, currency)}`;
  else return '';
};

const getEstimatedValue = value => {
  if (value) {
    const splitValue = value.toString().split('$');
    return splitValue && splitValue.length > 1
      ? Number(splitValue[1].split('+')[0])
      : Number(splitValue[0].split('+')[0]);
  }
  return 0;
};
const getContentfulItemTitle = item => {
  if (!item || !item.__typename) return '';
  switch (item.__typename) {
    case contentTypes.VEHICLE:
      return `${
        item.modelYear ? `${item.textYear ? 'c.' : ''} ${item.modelYear}` : ''
      } ${item?.make ? item.make.name : ''} ${item.model ? item.model : ''}`;
    case contentTypes.AUTOMOBILIA:
      return item.title;
    default:
      return '';
  }
};

const getAlgoliaItemTitle = vehicle => {
  if (vehicle.itemType === 'Automobilia') return vehicle.title;
  else return `${vehicle.modelYear} ${vehicle.make} ${vehicle.model}`;
};

const getAuctionYear = subEvents => {
  let minStartDate = null;

  subEvents.forEach(subEvent => {
    if (subEvent && subEvent.startDate) {
      const startDate = Date.parse(subEvent.startDate);
      if (!minStartDate || startDate < minStartDate) minStartDate = startDate;
    }
  });
  return new Date(minStartDate).getFullYear();
};

const getPriceFormatted = (
  price,
  currency = 'USD',
  maximumFractionDigits = 0
) => {
  if (!price || price === '0') return '';
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: maximumFractionDigits,
  }).format(price);
};
// Related-vehicles tuning. Ranking is done client-side (see getRelatedVehicles),
// so every tab fetches a broad, sold-only pool with the default sort.
const RELATED_HITS_PER_PAGE = 50;
const SAME_ERA_WINDOW = 5; // ± years considered the "same era"
const PRICE_BAND = 0.4; // ± fraction of the lot's sale price for "same price"

const changeActiveTab = (pageData, setFilters, selectedActiveTab) => {
  const queryFilter = {
    filtersInput: {
      make: [],
      auctionType: [],
      itemType: [],
      venue: [],
      auctionYear: [],
      soldOnly: true,
    },
    sortBy: 'DEFAULT',
    hitsPerPage: RELATED_HITS_PER_PAGE,
  };
  const modelYear = Number(pageData?.item?.modelYear);

  if (selectedActiveTab === TABS.sameBrand) {
    queryFilter.filtersInput.make = [pageData?.item?.make?.name];
  } else if (selectedActiveTab === TABS.sameEra) {
    queryFilter.filtersInput.year = {
      start: `${modelYear - SAME_ERA_WINDOW}`,
      end: `${modelYear + SAME_ERA_WINDOW}`,
    };
  } else if (selectedActiveTab === TABS.price) {
    const base = Number(pageData?.salePrice) || 0;
    queryFilter.filtersInput.estimate = base
      ? {
          start: `${Math.round(base * (1 - PRICE_BAND))}`,
          end: `${Math.round(base * (1 + PRICE_BAND))}`,
        }
      : { start: `${pageData?.lowEstimate}`, end: `${pageData?.highEstimate}` };
  }
  // else -> "all": no make/era/price filter, just sold lots; best overall
  // matches are surfaced by the composite ranking in getRelatedVehicles.
  setFilters(queryFilter);
};
const RELATED_POOL_SIZE = 6; // returned before cross-tab dedup + slice(0, 3)
const BRAND_MISMATCH_PENALTY = 1e6; // "all": prefer same make, but never require it
const MISSING_VALUE_PENALTY = 1e9; // push records with unusable data to the bottom

const tokenize = value =>
  `${value ?? ''}`
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

// Count of shared tokens between a candidate model name and the current lot's.
const modelTokenOverlap = (model, targetTokens) => {
  if (!targetTokens.length) return 0;
  const tokens = tokenize(model);
  return tokens.filter(token => targetTokens.includes(token)).length;
};

const yearDistance = (vehicle, targetYear) => {
  const year = Number(vehicle?.modelYear);
  if (!Number.isFinite(year) || !Number.isFinite(targetYear))
    return MISSING_VALUE_PENALTY;
  return Math.abs(year - targetYear);
};

// Normalised (fraction-of-target) gap so it can combine with year distance.
const priceDistance = (vehicle, targetPrice) => {
  const price = Number(vehicle?.salePrice);
  if (!Number.isFinite(price) || !targetPrice) return MISSING_VALUE_PENALTY;
  return Math.abs(price - targetPrice) / targetPrice;
};

const getRelatedVehicles = (filteredRelatedVehicles, activeTab, pageData) => {
  if (!filteredRelatedVehicles) return [];

  const currentId = pageData?.objectID;
  const targetYear = Number(pageData?.item?.modelYear);
  const targetPrice = Number(pageData?.salePrice) || 0;
  const targetMake = pageData?.item?.make?.name;
  const targetModelTokens = tokenize(pageData?.item?.model);

  // Exclude the current lot (by globally-unique objectID) and any unsold lot.
  const pool = filteredRelatedVehicles.filter(
    vehicle =>
      vehicle && vehicle.objectID !== currentId && Number(vehicle.salePrice) > 0
  );

  if (activeTab === TABS.sameEra) {
    pool.sort(
      (a, b) => yearDistance(a, targetYear) - yearDistance(b, targetYear)
    );
  } else if (activeTab === TABS.price) {
    pool.sort(
      (a, b) => priceDistance(a, targetPrice) - priceDistance(b, targetPrice)
    );
  } else if (activeTab === TABS.sameBrand) {
    // Model match dominates, then era, then price — so a Porsche 356 surfaces
    // other early Porsches rather than a modern Cayman.
    const score = vehicle =>
      -modelTokenOverlap(vehicle?.model, targetModelTokens) * 1000 +
      yearDistance(vehicle, targetYear) +
      priceDistance(vehicle, targetPrice);
    pool.sort((a, b) => score(a) - score(b));
  } else {
    // "all": soft same-make bonus + era + price, so results are never empty.
    const score = vehicle =>
      (vehicle?.make === targetMake ? 0 : BRAND_MISMATCH_PENALTY) +
      yearDistance(vehicle, targetYear) +
      priceDistance(vehicle, targetPrice);
    pool.sort((a, b) => score(a) - score(b));
  }

  return pool.slice(0, RELATED_POOL_SIZE);
};
const isBrowser = () => typeof window !== 'undefined';

const getLotTypeLabel = item => {
  if (item?.__typename === LOT_TYPES.VEHICLE) {
    return item.type === VEHICLE_TYPES.CAR
      ? LOT_TYPES_LABELS.CAR
      : LOT_TYPES_LABELS.MOTORCYCLE;
  } else if (item?.__typename === LOT_TYPES.AUTOMOBILIA) {
    return LOT_TYPES_LABELS.ITEM;
  }
  return LOT_TYPES_LABELS.CAR;
};

export {
  enabled,
  getRangeData,
  getTag,
  getAuction,
  getPrice,
  getEstimatedValue,
  MAX_ESTIMATE_PRICE,
  getAlgoliaItemTitle,
  getContentfulItemTitle,
  getAuctionYear,
  getPriceFormatted,
  PRIVATE_SALE_LISTING,
  getEstimates,
  changeActiveTab,
  getRelatedVehicles,
  isBrowser,
  getLotTypeLabel,
};
